import { notify } from '@affine/component';
import { LazyCodeEditor } from '@affine/core/modules/streamlain/workflow-viewer/editor';
import { stageBlob } from '@blocksuite/affine/shared/utils';
import { keymap } from '@codemirror/view';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AttachmentViewerBaseProps } from '../types';
import { getAttachmentBlob } from '../utils';
import * as styles from './text-viewer.css';

const MAX_TEXT_PREVIEW_BYTES = 5 * 1024 * 1024;

export const TextViewer = ({
  source,
  std,
  cleanupReplacedBlob,
  onTextEditorStateChange,
}: AttachmentViewerBaseProps) => {
  const [text, setText] = useState<string | null>(null);
  const [savedText, setSavedText] = useState('');
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);
  const [cleanupPending, setCleanupPending] = useState(false);
  const [resolvedSize, setResolvedSize] = useState<number | null>(source.size);
  const currentBlobIdRef = useRef(source.blobId);
  const textRef = useRef('');
  const cleanupReplacedBlobRef = useRef(cleanupReplacedBlob);
  const onTextEditorStateChangeRef = useRef(onTextEditorStateChange);

  cleanupReplacedBlobRef.current = cleanupReplacedBlob;
  onTextEditorStateChangeRef.current = onTextEditorStateChange;

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setSavedText('');
    setError(null);
    setCleanupPending(false);
    setResolvedSize(source.size);
    currentBlobIdRef.current = source.blobId;

    void getAttachmentBlob(source)
      .then(async blob => {
        if (!blob) {
          throw new Error('Text attachment data is unavailable');
        }
        if (blob.size > MAX_TEXT_PREVIEW_BYTES) {
          throw new Error('Text preview supports files up to 5 MB');
        }
        const value = await blob.text();
        if (!cancelled) {
          textRef.current = value;
          setText(value);
          setSavedText(value);
          setResolvedSize(blob.size);
        }
      })
      .catch(cause => {
        if (!cancelled) {
          setError(
            cause instanceof Error
              ? cause
              : new Error('Text attachment could not be opened')
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [source]);

  const canEdit =
    !!std &&
    source.canReplaceBlob !== false &&
    typeof source.replaceBlob === 'function';
  const dirty = text !== null && text !== savedText;

  const save = useCallback(async (): Promise<boolean> => {
    const currentBlobId = currentBlobIdRef.current;
    const nextText = textRef.current;
    if (
      !std ||
      !source.replaceBlob ||
      !currentBlobId ||
      saving ||
      nextText === savedText
    ) {
      return nextText === savedText;
    }

    setSaving(true);
    setCleanupPending(false);
    try {
      const file = new File([nextText], source.name, {
        type: source.mimeType || 'text/plain',
      });
      const nextBlobId = await stageBlob(std, file);

      if (nextBlobId !== currentBlobId) {
        await source.replaceBlob({
          blobId: nextBlobId,
          mimeType: file.type,
          size: file.size,
        });
        currentBlobIdRef.current = nextBlobId;
      }

      setSavedText(nextText);
      setResolvedSize(file.size);
      setSaving(false);
      notify.success({ title: 'Text file saved' });

      if (nextBlobId !== currentBlobId && cleanupReplacedBlobRef.current) {
        try {
          await cleanupReplacedBlobRef.current(currentBlobId);
        } catch (cleanupError) {
          console.error(
            '[attachment-text-editor] Replaced blob cleanup failed',
            cleanupError
          );
          setCleanupPending(true);
        }
      }
      return true;
    } catch (cause) {
      notify.error({
        title:
          cause instanceof Error
            ? cause.message
            : 'Text file could not be saved',
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [savedText, saving, source, std]);

  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  }, [save]);
  const editorExtensions = useMemo(
    () => [
      keymap.of([
        {
          key: 'Mod-s',
          preventDefault: true,
          run: () => {
            void saveRef.current();
            return true;
          },
        },
      ]),
    ],
    []
  );

  useEffect(() => {
    onTextEditorStateChangeRef.current?.({
      canEdit,
      dirty,
      saving,
      cleanupPending,
      resolvedSize,
      save,
    });
  }, [canEdit, cleanupPending, dirty, resolvedSize, save, saving]);

  useEffect(() => {
    return () => {
      onTextEditorStateChangeRef.current?.(null);
    };
  }, []);

  useEffect(() => {
    if (!dirty) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [dirty]);

  if (error) throw error;

  return (
    <div
      className={styles.viewport}
      data-testid="attachment-text-viewer"
      aria-busy={text === null}
    >
      <div className={styles.page}>
        <div className={styles.editorHost}>
          {text === null ? (
            <div className={styles.loading} />
          ) : (
            <LazyCodeEditor
              value={text}
              onChange={value => {
                textRef.current = value;
                setText(value);
              }}
              extensions={editorExtensions}
              readOnly={!canEdit}
              language="text"
              ariaLabel="Text file editor"
              fill
            />
          )}
        </div>
      </div>
    </div>
  );
};
